<template>
  <div>
    <p
      class="mb-4"
      style="font-size: 0.85rem; margin-left: 16px; font-weight: light !important"
      :style="template.secondaryTheme ? 'color: white;' : ''"
    >
      {{ searchPromptText }}
    </p>

    <div class="col form-group mb-5">
      <input
        ref="searchInput"
        id="searchInput"
        v-model="searchValue"
        v-mask="isFranceFlow ? null : cpfMask"
        class="w-100 c-input"
        :placeholder="searchPlaceholder"
        type="text"
        @keyup.enter="handleSearch"
        @input="handleInputChange"
      />
    </div>

    <div class="col form-group mb-2 mt-3">
      <input
        :value="searchResult ? searchResult.name : ''"
        class="w-100 c-input"
        style="opacity: 0.5"
        type="text"
        readonly
        disabled
        :placeholder="$t('searchUser.guardianName')"
      />
    </div>

    <div class="col form-group mt-4">
      <button
        class="btn btn-block shadow-sm btnStock btn"
        :style="template.secondaryTheme ? secondaryBtnStyleTheme : secondaryBtnStyle"
        type="button"
        :disabled="!canSearch"
        @click="handleSearch"
      >
        {{ $t('searchUser.search') }}
      </button>
    </div>

    <div v-if="isLoading" class="col form-group mt-4 text-center">
      <p :style="template.secondaryTheme ? 'color: white;' : ''">{{ $t('searchUser.searching') }}</p>
    </div>

    <div v-if="errorMessage" class="col form-group mt-3">
      <p
        style="font-size: 0.85rem; color: #ff637e"
        :style="template.secondaryTheme ? 'color: #ff637e;' : ''"
      >
        {{ errorMessage }}
      </p>
    </div>

    <b-modal
      id="search-user-invalid-account-modal"
      v-model="showInvalidAccountModal"
      centered
      hide-header-close
      hide-footer
      :no-close-on-backdrop="false"
      :no-close-on-esc="true"
      :header-class="'border-0'"
      :content-class="'search-user-modal-content'"
      :style="'--modal-bg-color: ' + (template.secondaryColor || '#ffffff')"
    >
      <template #modal-header>
        <p class="modal-title w-100 text-center mb-4 search-user-modal-text search-user-modal-title">
          {{ $t('searchUser.invalidAccountTitle') }}
        </p>
      </template>

      <p class="mb-4 text-center search-user-modal-text search-user-modal-message">
        <template v-if="isFranceFlow">
          {{ $t('searchUser.invalidAccountMessageEmail') }}
        </template>
        <template v-else>
          {{ $t('searchUser.invalidAccountMessageCpf') }}
        </template>
      </p>
      <p class="mb-5 text-center search-user-modal-text search-user-modal-value">
        <template v-if="isFranceFlow">
          {{ $t('searchUser.emailEntered') }} {{ displaySearchValueInAlert }}
        </template>
        <template v-else>
          {{ $t('searchUser.cpfEntered') }} {{ displaySearchValueInAlert }}
        </template>
      </p>

      <div class="d-flex flex-column align-items-center mt-4 px-4" style="gap: 6px;">
        <button
          type="button"
          class="btn btn-block shadow-sm search-user-modal-btn-primary"
          :style="primaryBtnStyle"
          @click="onInvalidAccountTryAgain"
        >
          {{ $t('searchUser.tryAgain') }}
        </button>
        <button
          type="button"
          class="btn btn-block shadow-sm search-user-modal-btn-secondary"
          @click="onInvalidAccountRegisterGuardian"
        >
          {{ $t('searchUser.registerGuardian') }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { mask } from 'vue-the-mask'
import { loginFB } from '../../services/ServFB.js'

const lFB = new loginFB()

export default {
  name: 'SearchUser',
  props: {
    validate: {
      type: Boolean,
      required: true,
    },
  },
  mask: {
    ...mask,
    tokens: {
      ...mask.tokens,
      null: /./,
    },
  },
  data: () => ({
    obj: {},
    searchValue: '',
    isLoading: false,
    searchResult: null,
    errorMessage: '',
    cpfMask: null,
    toastMessage: '',
    showInvalidAccountModal: false,
    invalidAccountMessage: '',
    displaySearchValueInAlert: '',
    blockAdvanceAfterInvalidAccount: false,
  }),
  computed: {
    ...mapGetters(['primaryBtnStyle', 'secondaryBtnStyle', 'secondaryBtnStyleTheme']),
    ...mapState(['template', 'registrationObject', 'config']),
    isFranceFlow() {
      return String((this.config && this.config.iso3) || '').toUpperCase() === 'FRA'
    },
    searchPromptText() {
      return this.isFranceFlow
        ? this.$t('searchUser.searchPromptEmailOnly')
        : this.$t('searchUser.searchPrompt')
    },
    searchPlaceholder() {
      return this.isFranceFlow
        ? this.$t('searchUser.emailOnly')
        : this.$t('searchUser.cpfOrEmail')
    },
    clientId() {
      const route = this.$route
      const fromUrl = route && route.query && (route.query.client_id || route.query.clientId)
      if (fromUrl) return fromUrl
      const config = this.config
      if (!config) return ''
      if (config.sso && config.sso.clientId) return config.sso.clientId
      return config.name || ''
    },
    canSearch() {
      if (!this.searchValue || !this.searchValue.trim()) {
        return false
      }
      const trimmedValue = this.searchValue.trim()
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)

      if (this.isFranceFlow) {
        return isEmail
      }

      const isCPF =
        /^[\d.\- ]+$/.test(trimmedValue) && trimmedValue.replace(/[^\d]/g, '').length >= 11
      return isCPF || isEmail
    },
  },
  watch: {
    validate(newValue) {
      if (newValue) this.validateFields()
    },
    searchValue(newValue) {
      if (this.isFranceFlow) {
        this.cpfMask = null
        return
      }

      const trimmedValue = newValue.trim()
      const isEmail = /@/.test(trimmedValue)

      if (isEmail) {
        this.cpfMask = null
      } else {
        const cleanValue = trimmedValue.replace(/[^\d]/g, '')
        if (cleanValue.length <= 11 && /^\d+$/.test(cleanValue)) {
          this.cpfMask = '###.###.###-##'
        } else {
          this.cpfMask = null
        }
      }
    },
  },
  created() {
    this.obj = JSON.parse(JSON.stringify(this.registrationObject))
  },
  mounted() {
    if (this.$refs.searchInput) {
      this.$refs.searchInput.focus()
    }
  },
  methods: {
    ...mapMutations([
      'updateRegistrationObject',
      'clearRegistrationErrors',
      'addRegistrationError',
    ]),
    handleInputChange() {
      this.errorMessage = ''
      this.searchResult = null
    },
    getSearchValidationError() {
      const trimmedValue = (this.searchValue || '').trim()

      if (!trimmedValue) {
        return this.$t('searchUser.guardianRequired')
      }

      if (this.isFranceFlow) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
          return this.$t('searchUser.emailRequired')
        }
        return null
      }

      if (!this.canSearch) {
        return this.$t('searchUser.cpfOrEmailInvalid')
      }

      const cpfDigits = trimmedValue.replace(/\D/g, '')
      if (cpfDigits.length !== 11) {
        return this.$t('searchUser.cpfRequired')
      }

      return null
    },
    async handleSearch() {
      if (!this.canSearch) {
        return
      }

      const validationError = this.getSearchValidationError()
      if (validationError) {
        this.errorMessage = validationError
        this.toastMessage = validationError
        this.makeToast('warning')
        return
      }

      this.isLoading = true
      this.errorMessage = ''
      this.searchResult = null

      try {
        await this.checkGuardianApi()
      } catch (error) {
        this.errorMessage =
          (error &&
            error.response &&
            error.response.data &&
            error.response.data.header &&
            error.response.data.header.msg) ||
          'Erro ao pesquisar. Tente novamente.'
        this.toastMessage = this.errorMessage
        this.makeToast('danger')
      } finally {
        this.isLoading = false
      }
    },
    async validateFields() {
      this.$emit('validating')
      this.clearRegistrationErrors()
      let hasErrors = false

      if (!this.searchValue || !this.searchValue.trim()) {
        hasErrors = true
        const warningMsg = this.$t('searchUser.guardianRequired')
        this.addRegistrationError({ msg: warningMsg })
        this.toastMessage = warningMsg
        this.makeToast('warning')
      } else {
        const validationError = this.getSearchValidationError()
        if (validationError) {
          hasErrors = true
          this.addRegistrationError({ msg: validationError })
          this.toastMessage = validationError
          this.makeToast('warning')
        }
      }

      if (!hasErrors) {
        if (!this.searchResult) {
          this.isLoading = true
          try {
            await this.checkGuardianApi()
          } catch (error) {
            hasErrors = true
            this.errorMessage =
              (error &&
                error.response &&
                error.response.data &&
                error.response.data.header &&
                error.response.data.header.msg) ||
              'Erro ao pesquisar. Tente novamente.'
            this.toastMessage = this.errorMessage
            this.addRegistrationError({ msg: this.errorMessage })
            this.makeToast('danger')
          } finally {
            this.isLoading = false
          }
          if (!hasErrors && this.blockAdvanceAfterInvalidAccount) {
            hasErrors = true
          }
          if (!hasErrors && this.errorMessage && !this.searchResult) {
            hasErrors = true
            this.addRegistrationError({ msg: this.errorMessage })
          }
        }

        if (this.searchResult && !hasErrors) {
          let identityNumber =
            typeof this.searchResult.identity === 'string'
              ? parseInt(this.searchResult.identity.replace(/[^\d]/g, ''), 10)
              : parseInt(this.searchResult.identity, 10)

          if (Number.isNaN(identityNumber) || identityNumber <= 0) {
            hasErrors = true
            this.errorMessage = 'Erro: Identidade inválida retornada pela pesquisa.'
            this.toastMessage = this.errorMessage
            this.addRegistrationError({ msg: this.errorMessage })
            this.makeToast('danger')
            return
          }

          this.obj.searchIdentity = {
            identity: 0,
            numberType: 14,
            number: String(identityNumber),
            verified: null,
          }

          this.obj.searchResultName = this.searchResult.name

          this.$store.commit('updateRegistrationObject', this.obj)
          this.$emit('next', { guardianResolvedViaSearch: true })
        }
      }
    },
    async checkGuardianApi() {
      const ownerId = parseInt(this.config && this.config.ownerId, 10)
      const systemId = parseInt(this.config && this.config.systemID, 10)
      const trimmedValue = this.searchValue.trim()

      let number
      let numberType

      if (this.isFranceFlow) {
        number = trimmedValue
        numberType = 4
      } else {
        number = trimmedValue.replace(/\D/g, '')
        numberType = 1
      }

      if (
        !number ||
        (!this.isFranceFlow && number.length !== 11) ||
        !ownerId ||
        !systemId ||
        !this.clientId
      ) {
        const msg = 'Configuração incompleta (ownerId, systemId ou clientId).'
        this.errorMessage = msg
        throw new Error(msg)
      }

      const payload = {
        number,
        numberType,
        ownerId,
        clientId: this.clientId,
        systemId,
      }

      const response = await lFB.checkGuardian(payload)

      if (!response || !response.header || response.header.codigo !== 1) {
        this.errorMessage =
          (response && response.header && response.header.msg) || 'Erro ao consultar responsável.'
        throw new Error(this.errorMessage)
      }

      const { action, actionData } = response.data || {}

      if (action === 'Success' && actionData && actionData.identity) {
        this.searchResult = {
          name: actionData.name || '',
          identity: actionData.identity,
        }
        this.errorMessage = ''
        this.blockAdvanceAfterInvalidAccount = false
        this.$emit('search-result', this.searchResult)
        return
      }

      if (action === 'InvalidAccount') {
        this.searchResult = null
        this.errorMessage = ''
        this.invalidAccountMessage =
          (actionData && actionData.message) || 'Não foi possível localizar o responsável.'
        this.displaySearchValueInAlert = this.searchValue.trim()
        this.blockAdvanceAfterInvalidAccount = true
        this.showInvalidAccountModal = true
        return
      }

      this.searchResult = null
      this.blockAdvanceAfterInvalidAccount = false
      this.errorMessage =
        (actionData && actionData.message) || 'Não foi possível localizar o responsável.'
      this.toastMessage = this.errorMessage
      this.makeToast('warning')
    },
    onInvalidAccountTryAgain() {
      this.showInvalidAccountModal = false
      this.blockAdvanceAfterInvalidAccount = false
      this.clearRegistrationErrors()
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus()
        }
      })
    },
    onInvalidAccountRegisterGuardian() {
      this.showInvalidAccountModal = false
      this.blockAdvanceAfterInvalidAccount = false
      this.clearRegistrationErrors()
      this.$emit('goto-guardian-from-search')
    },
    makeToast(variant = null) {
      this.$bvToast.toast(this.toastMessage || 'Erro ao pesquisar', {
        title: this.$t('common.attention'),
        variant: variant,
        solid: true,
      })
    },
  },
}
</script>

<style scoped>
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>

<style>
.search-user-modal-content {
  background-color: var(--modal-bg-color, #ffffff) !important;
  border: none;
  border-radius: 12px;
  color: #000 !important;
}
.search-user-modal-content .modal-header,
.search-user-modal-content .modal-body,
.search-user-modal-content .modal-footer {
  background-color: transparent !important;
}
.search-user-modal-text {
  color: #000 !important;
}
.search-user-modal-title {
  font-size: 1.4rem;
  font-weight: 600;
}
.search-user-modal-message {
  font-size: 0.9rem;
}
.search-user-modal-value {
  font-size: 1.2rem;
  font-weight: 500;
}
.search-user-modal-btn-primary {
  font-size: 1.1rem;
  padding: 10px 12px;
  font-weight: 500;
}
.search-user-modal-btn-secondary {
  border: 1.5px solid #000 !important;
  font-size: 1.1rem;
  padding: 10px 16px;
  font-weight: 500;
  margin-bottom: 3rem;
  color: #000 !important;
  background-color: transparent !important;
}
</style>
